import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  useConnectorSummaries,
  useConfigureConnector,
  useDisconnectConnector,
} from '../../../../api/hooks/settings.hook';
import type { ConnectorSummary } from '../../../../api/services/settings';
import { JsonEditor } from '../../../../components/common/JsonEditor';
import { formatDateTime } from './shared';

type ConnectorCardProps = {
  summary: ConnectorSummary;
  onSave: (provider: string, metadata: Record<string, unknown>) => Promise<void>;
  onDisconnect: (provider: string) => Promise<void>;
};

const ConnectorCard: React.FC<ConnectorCardProps> = ({ summary, onSave, onDisconnect }) => {
  const [editorValue, setEditorValue] = useState(
    JSON.stringify(summary.metadata ?? {}, null, 2) || '{}',
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditorValue(JSON.stringify(summary.metadata ?? {}, null, 2) || '{}');
  }, [summary.metadata]);

  const handleSave = async () => {
    try {
      const parsed = JSON.parse(editorValue || '{}');
      await onSave(summary.provider, parsed);
      setError(null);
      toast.success(`Saved configuration for ${summary.provider}.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to save connector.';
      setError(message);
      toast.error(message);
    }
  };

  const handleDisconnect = async () => {
    try {
      await onDisconnect(summary.provider);
      toast.success(`Disconnected ${summary.provider}.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to disconnect.';
      toast.error(message);
    }
  };

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden shadow-sm">
      <div className="flex items-center justify-between bg-neutral-50 px-4 py-3 border-b border-neutral-200">
        <div>
          <h3 className="text-sm font-semibold text-neutral-900 capitalize">
            {summary.provider.replace(/_/g, ' ')}
          </h3>
          <p className="text-xs text-neutral-500">
            Status: {summary.connected ? 'Connected' : 'Not connected'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-500">
            Last update: {formatDateTime(summary.updated_at)}
          </span>
          <button
            type="button"
            onClick={handleDisconnect}
            className="text-xs font-medium text-neutral-600 hover:text-neutral-900"
            disabled={!summary.connected}
          >
            Disconnect
          </button>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <JsonEditor value={editorValue} onChange={setEditorValue} height={220} />
        {error ? <p className="text-xs text-red-600">{error}</p> : null}
        <div className="flex justify-end">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500"
            onClick={handleSave}
          >
            Save configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export const ConnectorsConfiguration = () => {
  const summaries = useConnectorSummaries();
  const configure = useConfigureConnector();
  const disconnect = useDisconnectConnector();

  const handleSave = async (provider: string, metadata: Record<string, unknown>) => {
    await configure.mutateAsync({ provider, metadata });
  };

  const handleDisconnect = async (provider: string) => {
    await disconnect.mutateAsync(provider);
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-4 sm:space-y-6">
        <div className="space-y-1">
          <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">
            Connector configuration
          </h1>
          <p className="text-sm text-neutral-600">
            Manage data connectors used to populate the workspace knowledge base.
          </p>
        </div>
        <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-4">
          {summaries.isLoading ? (
            <div className="text-neutral-500 text-sm">Loading connectors…</div>
          ) : summaries.data && summaries.data.length > 0 ? (
            <div className="grid grid-cols-1 gap-5">
              {summaries.data.map((summary) => (
                <ConnectorCard
                  key={summary.provider}
                  summary={summary}
                  onSave={handleSave}
                  onDisconnect={handleDisconnect}
                />
              ))}
            </div>
          ) : (
            <div className="text-sm text-neutral-500">
              No connectors are registered yet. Configure a connector to sync external data sources.
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
